import { type HousingSearchForm } from "~/HousingSelectionAssistant/types/housingSAFormTypes";
import { NumberSystem, TemperatureUnit } from "~/HousingSelectionAssistant/types/types";
import {
  HOUSING_SA_DIMENIONS_CODES,
  HOUSING_SA_MULTI_FACET_CODES,
  HOUSING_SA_SINGLE_FACET_CODES,
  HOUSING_SA_TEMPERATURE_CODES,
  HOUSING_SA_TRUE_CLUSTERS,
} from "~/HousingSelectionAssistant/constants";
import { NumberFormatter as NF } from "~/utils/NumberFormatter";
import { VALID_INPUT_NUMBER } from "~/utils/validations/isNumber";
import { type RuntimeFilterDefinition } from "~/HousingSelectionAssistant/types/FilterDefinitionV2";

type ProcessingMode = "FACET_SINGLE" | "FACET_MULTI" | "TECH_VALUE" | "TEMP_VALUE";

const codeByProcessingMode: Record<string, ProcessingMode> = {};
HOUSING_SA_SINGLE_FACET_CODES.forEach((code) => (codeByProcessingMode[code] = "FACET_SINGLE"));
HOUSING_SA_MULTI_FACET_CODES.forEach((code) => (codeByProcessingMode[code] = "FACET_MULTI"));
HOUSING_SA_DIMENIONS_CODES.forEach((code) => (codeByProcessingMode[code] = "TECH_VALUE"));
HOUSING_SA_TEMPERATURE_CODES.forEach((code) => (codeByProcessingMode[code] = "TEMP_VALUE"));
HOUSING_SA_TRUE_CLUSTERS.forEach((code) => (codeByProcessingMode[code] = "FACET_MULTI"));

export function getUrlParamsFromFilters(
  data: HousingSearchForm,
  filterMap: Record<string, RuntimeFilterDefinition>,
  pageSize?: number
) {
  const params = new URLSearchParams();
  params.append("isSelectionAssistant", "HOUSINGS");

  const assistantValues: string[] = [];
  const techValueParts: string[] = [];
  const qParts: string[] = [];

  const makeProcessFacet = (getter: (code: string) => string[]) => (code: string) => {
    const formData = getter(code);
    const tiles = filterMap[code]?.items;
    formData.forEach((value: string) => {
      const tile = tiles?.find((tile) => tile.code === value);
      if (tile) {
        qParts.push(`${tile.facetCode}:${encodeURIComponent(tile.facetValueCode)}`);
      } else {
        console.warn(`Could not find tile for code: ${code} and value: ${value}`);
        qParts.push(`${code}:${encodeURIComponent(value)}`);
      }
    });
  };

  const addTechValuePart = (
    code: string,
    strVal: string,
    type: "min" | "max",
    isImperial: boolean,
    positiveOnly: boolean
  ) => {
    const numVal = getNumber(strVal);
    if (numVal !== null && (!positiveOnly || numVal > 0)) {
      const fullCode = isImperial ? `${code}imperial_${type}` : `${code}_${type}`;
      techValueParts.push(fullCode + ":" + numVal.toString());
    }
  };

  const makeProcessTechValue = (isImperial: boolean, positiveOnly: boolean) => (code: string) => {
    const { min, max } = data[code] ?? {};
    addTechValuePart(code, min, "min", isImperial, positiveOnly);
    addTechValuePart(code, max, "max", isImperial, positiveOnly);
  };

  const processors: Record<ProcessingMode, (code: string) => void> = {
    FACET_SINGLE: makeProcessFacet((code) => {
      const val = data[code] as string;
      return val ? [val] : [];
    }),
    FACET_MULTI: makeProcessFacet((code) => (data[code] as string[]) ?? []),
    TECH_VALUE: makeProcessTechValue(data["dimensionUnit"] === NumberSystem.Imperial, true),
    TEMP_VALUE: makeProcessTechValue(data["temperatureUnit"] === TemperatureUnit.Fahrenheit, false),
  };

  Object.keys(data).forEach((code) => {
    const mode = codeByProcessingMode[code];
    if (mode) {
      processors[mode](code);
    }
  });

  params.append("assistantValues", assistantValues.join(","));
  if (techValueParts.length > 0) {
    params.set("techValues", techValueParts.join(","));
  }
  params.set("q", `:${data.sortCode}:${qParts.join(":")}`);

  params.set("sort", data.sortCode);
  params.set("page", data.page + "");
  if (pageSize !== undefined) {
    params.set("pageSize", pageSize + "");
  }

  return params.toString();
}

export const getNumber = (stringVal: string) => {
  stringVal = stringVal.trim();
  if (stringVal === "" || !validateNumber(stringVal)) {
    return null;
  }
  return +stringVal.replaceAll(NF.getThousandSeparator(), "").replaceAll(NF.getDecimalSeparator(), ".");
};

const validateNumber = (stringVal: string) => {
  return VALID_INPUT_NUMBER.test(stringVal);
};

const MM_PER_INCH = 25.4;

function convertMmToInch(value: number) {
  return value / MM_PER_INCH;
}

function convertInchToMm(value: number) {
  return value * MM_PER_INCH;
}

function convertFahrenheitToCelsius(value: number) {
  return ((value - 32) * 5) / 9;
}
